import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../contexts/AuthContext"
import type { Amenity, Spot, SpotCreate } from "../types"

const API_URL = import.meta.env.VITE_API_URL || "/api"

const AMENITY_OPTIONS: { key: Amenity; icon: string }[] = [
  { key: "water", icon: "💧" },
  { key: "electricity", icon: "⚡" },
  { key: "wc", icon: "🚻" },
  { key: "shower", icon: "🚿" },
  { key: "wifi", icon: "📶" },
  { key: "waste_disposal", icon: "🗑️" },
  { key: "pet_friendly", icon: "🐕" },
  { key: "bbq", icon: "🔥" },
  { key: "shade", icon: "🌳" },
  { key: "flat", icon: "📐" },
  { key: "quiet", icon: "🔇" },
]

interface SpotFormProps {
  latitude?: number
  longitude?: number
  onSaved: (spot: Spot) => void
  onCancel: () => void
}

export default function SpotForm({ latitude, longitude, onSaved, onCancel }: SpotFormProps) {
  const { t } = useTranslation()
  const { auth } = useAuth()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState<SpotCreate["category"]>("free_wild")
  const [lat, setLat] = useState(latitude?.toString() || "")
  const [lng, setLng] = useState(longitude?.toString() || "")
  const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([])
  const [price, setPrice] = useState("")
  const [maxStay, setMaxStay] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleAmenity = (a: Amenity) => {
    setSelectedAmenities((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const latNum = parseFloat(lat)
    const lngNum = parseFloat(lng)
    const priceNum = price.trim() ? parseFloat(price) : undefined
    const maxStayNum = maxStay.trim() ? parseInt(maxStay, 10) : undefined

    if (!name.trim() || isNaN(latNum) || isNaN(lngNum)) {
      setError(t("error.required"))
      return
    }

    const payload: SpotCreate = {
      name: name.trim(),
      description: description.trim() || undefined,
      category,
      latitude: latNum,
      longitude: lngNum,
      amenities: selectedAmenities.length > 0 ? selectedAmenities : undefined,
      price_per_night: priceNum,
      max_stay_hours: maxStayNum,
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }
    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`
    }

    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/spots`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || t("error.generic"))
      }

      const spot: Spot = await res.json()
      onSaved(spot)
    } catch (err) {
      setError(err instanceof Error ? err.message : t("error.generic"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40">
      <div className="h-full w-full flex items-center justify-center p-0 sm:p-4">
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-slate-900 w-full max-w-lg h-full sm:h-auto sm:max-h-[85vh] sm:rounded-2xl shadow-2xl overflow-y-auto p-5 space-y-4"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehaviorY: "contain" }}
        >
          <div className="flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 py-2 z-10">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t("map.addSpot")}</h2>
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl p-2"
            >
              &times;
            </button>
          </div>

          {error && (
            <p className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{error}</p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("spot.name")} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("spot.description")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("spot.category")}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as SpotCreate["category"])}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="free_wild">{t("spot.categories.free_wild")}</option>
              <option value="paid_campsite">{t("spot.categories.paid_campsite")}</option>
              <option value="rest_area">{t("spot.categories.rest_area")}</option>
              <option value="service_point">{t("spot.categories.service_point")}</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Lat *
              </label>
              <input
                type="number"
                step="any"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Lng *
              </label>
              <input
                type="number"
                step="any"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t("spot.price")} (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={t("spot.free")}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t("spot.maxStay")} (h)
              </label>
              <input
                type="number"
                min="0"
                value={maxStay}
                onChange={(e) => setMaxStay(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Amenities
            </label>
            <div className="flex flex-wrap gap-2">
              {AMENITY_OPTIONS.map((a) => {
                const active = selectedAmenities.includes(a.key)
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => toggleAmenity(a.key)}
                    className={`px-3 py-2 rounded-xl text-sm border transition-colors ${
                      active
                        ? "bg-steel/10 dark:bg-steel/20 border-steel/30 dark:border-steel/40 text-steel-dark dark:text-steel-light"
                        : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="mr-1">{a.icon}</span>
                    {t(`amenity.${a.key}`)}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex gap-3 pt-2 pb-20">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-4 rounded-xl bg-steel text-white font-medium hover:bg-steel-dark disabled:opacity-50 transition-colors text-lg"
            >
              {loading ? t("spot.saving") : t("spot.save")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-4 rounded-xl bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-gray-200 font-medium hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors text-lg"
            >
              {t("spot.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
